import React, { useContext, useMemo, useCallback, useState } from 'react';
import { BaseCompAction, capitalizeAll, SlideOutComp } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { Entity } from '@moderepo/mode-apis';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core';
import { UsageMode } from '@moderepo/mode-ui-state';
import { useRitzTranslation } from '../../utils/customHooks';
import { EntitiesListingContainer } from './EntitiesListingContainer';
import { EntitiesListingViewType } from '../../components';

const useStyle = makeStyles(() => {
    return {
        entitiesListingContainer: {
            height: '100%',
        },
    };
});


interface SelectEntityContainerProps {
    readonly projectId: number;
    readonly parentId?: string | undefined;
    readonly onEntitySelected: (entity: Entity)=> void;
}


/**
 * This Component is a Slide Out component used for asking the user to select an entity. This component will use the 'EntitiesListingContainer'
 * to display the entity tree and ask the user to select an entity from the tree. Once an entity is selected, this component will pass the selected
 * entity to the handler.
 */
export const SelectEntityContainer: React.FC<SelectEntityContainerProps> = (props: SelectEntityContainerProps) => {
    const { projectId } = props;
    const { state:dataState } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();

    // Local state to keep track of the current selected entity from the list
    const [selectedEntity, setSelectedEntity] = useState<Entity | undefined>();


    /**
     * On entity selected from the list of entities, we will remember the selected entity in the local state but will not show the entity's
     * details until the user click the 'Show selected entity' button.
     */
    const onEntitySelected = useCallback((entity: Entity) => {
        setSelectedEntity(entity);
    }, []);


    /**
     * Create an Array of primary actions for the Entity
     */
    const primaryActions = useMemo(() => {
        const actions: BaseCompAction[] = [{
            label   : capitalizeAll(trans('entity.action.show_entity_name_details', { entity_name: selectedEntity?.displaySettings.name ?? '' })),
            disabled: !selectedEntity,
            onClick : () => {
                if (selectedEntity) {
                    props.onEntitySelected(selectedEntity);
                }
            },
        }];
        return actions;
    }, [props, selectedEntity, trans]);


    /**
     * Create secondary actions for the Entity
     */
    const secondaryActions = useMemo(() => {
        return undefined;
    }, []);



    /**
     * Create the content for the slide-out panel. The content will just going to be the EntitiesListingContainer
     */
    const slideOutCompContent = useMemo(() => {
        return (
            <EntitiesListingContainer
                className={clsx(classes.entitiesListingContainer, 'transparent')}
                projectId={projectId}
                parentId={props.parentId}
                disablePreviewOption
                minimalUI
                canCreateEntities={false}
                viewType={EntitiesListingViewType.TREE}
                usageMode={UsageMode.MANAGE_DATA}
                onEntitySelected={onEntitySelected}
            />
        );
    }, [classes.entitiesListingContainer, onEntitySelected, projectId, props.parentId]);



    return (
        <SlideOutComp
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
            compContent={slideOutCompContent}
            closable
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
